//© all rights reserved to 6over6 Ltd. 2022. This software (the "Software") is
the property of 6over6 Ltd. and its licensors. Any use of the Software is
strictly prohibited unless an explicit license or permission is granted by
6over6 Ltd.
<template>
  <div>
    <div id="go_markers-targets-container">
      <svg
        class="go_markersTargets"
        :width="`${data.overall_width}px`"
        :height="`${data.overall_height}px`"
        style="align-self: center; z-index: 1"
        :viewBox="`0 0 ${data.overall_width} ${data.overall_height}`"
      >
        <DashedBorders
          v-if="data.showBoundaries"
          :boundaryInfo="boundaryInfo"
        />
        <MarkerSvg
          v-for="(markerInfo, key) in markersInfo.markers"
          :key="key"
          :markerInfo="markerInfo"
          :radius="markersInfo.radius"
          :styles="markersInfo.styles"
        />
        <template v-if="data.targets">
          <TargetSvg
            v-for="(target, index) in data.targets['left']['targets']"
            :key="`left-${index}`"
            :target="target"
            :general="data.targets['left']"
          />
          <MultiFocalLines
            v-if="data.showMultifocalLines"
            :lines="{
              width: data.lines['lines_width_px'],
              coordinates: data.lines.left,
            }"
          />
          <TargetSvg
            v-for="(target, index) in data.targets['right']['targets']"
            :key="`right-${index}`"
            :target="target"
            :general="data.targets['right']"
          />
          <MultiFocalLines
            v-if="data.showMultifocalLines"
            :lines="{
              width: data.lines['lines_width_px'],
              coordinates: data.lines.right,
            }"
          />
        </template>
        <!--left focus area-->
        <svg
          v-if="data.focus_area"
          :width="data.focus_area['position']['width']"
          :height="data.focus_area['position']['height']"
          :x="data.focus_area['position']['left']['x']"
          :y="data.focus_area['position']['left']['y']"
        >
          <line
            :x1="data.focus_area['thickness'] / 2"
            y1="0"
            :x2="data.focus_area['thickness'] / 2"
            :y2="data.focus_area['length']"
            :style="`stroke:black;stroke-width:${data.focus_area['thickness']}`"
          />
          <line
            :x1="data.focus_area['thickness'] * 2.5"
            y1="0"
            :x2="data.focus_area['thickness'] * 2.5"
            :y2="data.focus_area['length']"
            :style="`stroke:black;stroke-width:${data.focus_area['thickness']}`"
          />
          <line
            :x1="data.focus_area['thickness'] * 4.5"
            y1="0"
            :x2="data.focus_area['thickness'] * 4.5"
            :y2="data.focus_area['length']"
            :style="`stroke:black;stroke-width:${data.focus_area['thickness']}`"
          />
        </svg>

        <!--right focus area-->
        <svg
          v-if="data.focus_area"
          :width="data.focus_area['position']['width']"
          :height="data.focus_area['position']['height']"
          :x="data.focus_area['position']['right']['x']"
          :y="data.focus_area['position']['right']['y']"
        >
          <line
            x1="0"
            :y1="data.focus_area['thickness'] / 2"
            :x2="data.focus_area['length']"
            :y2="data.focus_area['thickness'] / 2"
            :style="`stroke:black;stroke-width:${data.focus_area['thickness']}`"
          />
          <line
            x1="0"
            :y1="
              data.focus_area['thickness'] / 2 +
              data.focus_area['thickness'] * 2
            "
            :x2="data.focus_area['length']"
            :y2="
              data.focus_area['thickness'] / 2 +
              data.focus_area['thickness'] * 2
            "
            :style="`stroke:black;stroke-width:${data.focus_area['thickness']}`"
          />
          <line
            x1="0"
            :y1="
              data.focus_area['thickness'] / 2 +
              data.focus_area['thickness'] * 4
            "
            :x2="data.focus_area['length']"
            :y2="
              data.focus_area['thickness'] / 2 +
              data.focus_area['thickness'] * 4
            "
            :style="`stroke:black;stroke-width:${data.focus_area['thickness']}`"
          />
        </svg>
      </svg>
    </div>
  </div>
</template>
<script setup>
import ResolutionService from "../../services/ResolutionService.js";

import DashedBorders from "@/components/markersAndTargets/DashedBorders.vue";
import MarkerSvg from "@/components/markersAndTargets/MarkerSvg.vue";
import TargetSvg from "@/components/markersAndTargets/TargetSvg.vue";
import MultiFocalLines from "@/components/markersAndTargets/MultiFocalLines.vue";

import { onMounted, computed } from "vue";
import { useStore } from "vuex";
const store = useStore();

onMounted(function () {
  ResolutionService.check(store);
});

const data = computed(function () {
  return store.state.page.data;
});

const markersInfo = computed(function () {
  return {
    radius: {
      outer: `${data.value.marker_outer_radius_px}px`,
      inner: `${data.value.marker_radius_px}px`,
    },
    styles: {
      outer: {
        fill: "#0000FF",
      },
      inner: {
        fill: data.value.green,
      },
    },
    markers: {
      topLeft: {
        cx: `${data.value.marker_outer_radius_px}px`,
        cy: `${data.value.marker_outer_radius_px}px`,
      },
      topMiddle: {
        cx: `${
          data.value.marker_outer_radius_px + data.value.marker2marker_width_px
        }px`,
        cy: `${data.value.marker_outer_radius_px}px`,
      },
      topRight: {
        cx: `${
          data.value.marker_outer_radius_px +
          data.value.marker2marker_width_px * 2
        }px`,
        cy: `${data.value.marker_outer_radius_px}px`,
      },
      bottomLeft: {
        cx: `${data.value.marker_outer_radius_px}px`,
        cy: `${
          data.value.marker_outer_radius_px + data.value.marker2marker_height_px
        }px`,
      },
      bottomMiddle: {
        cx: `${
          data.value.marker_outer_radius_px + data.value.marker2marker_width_px
        }px`,
        cy: `${
          data.value.marker_outer_radius_px + data.value.marker2marker_height_px
        }px`,
      },
      bottomRight: {
        cx: `${
          data.value.marker_outer_radius_px +
          data.value.marker2marker_width_px * 2
        }px`,
        cy: `${
          data.value.marker_outer_radius_px + data.value.marker2marker_height_px
        }px`,
      },
    },
  };
});

const boundaryInfo = computed(function () {
  return {
    coordinates: {
      top: {
        x1: data.value.marker_outer_radius_px,
        y1: data.value.marker_outer_radius_px,
        x2:
          data.value.marker_outer_radius_px +
          data.value.marker2marker_width_px * 2,
        y2: data.value.marker_outer_radius_px,
      },
      left: {
        x1: data.value.marker_outer_radius_px,
        y1: data.value.marker_outer_radius_px,
        x2: data.value.marker_outer_radius_px,
        y2:
          data.value.marker_outer_radius_px +
          data.value.marker2marker_height_px,
      },
      bottom: {
        x1: data.value.marker_outer_radius_px,
        y1:
          data.value.marker_outer_radius_px +
          data.value.marker2marker_height_px,
        x2:
          data.value.marker_outer_radius_px +
          data.value.marker2marker_width_px * 2,
        y2:
          data.value.marker_outer_radius_px +
          data.value.marker2marker_height_px,
      },
      right: {
        x1:
          data.value.marker_outer_radius_px +
          data.value.marker2marker_width_px * 2,
        y1: data.value.marker_outer_radius_px,
        x2:
          data.value.marker_outer_radius_px +
          data.value.marker2marker_width_px * 2,
        y2:
          data.value.marker_outer_radius_px +
          data.value.marker2marker_height_px,
      },
    },
    styles: {
      strokeDasharray: `${data.value.marker_outer_radius_px / 5}, ${
        data.value.marker_outer_radius_px
      }`,
      strokeWidth: data.value.marker_radius_px / 10,
      stroke: "black",
    },
  };
});
</script>
